import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  NewPhysicianCreated,
  NewPhysicianParams,
  PatientAvailablePhysicians,
  PatientId,
  PatientPrimaryPhysician,
  PhysicianId,
} from "../../messages";
import { translate } from "../../translate";
import { STRINGS } from "../../strings";
import { AppCard, AppCardContent, AppCardHeader, AppCardTitle } from "../Generic/AppCard";
import Flex from "../Generic/Flex";
import LoadingBar from "../Generic/LoadingBar";
import Label from "../Label";
import { queryClient } from "../../App";
import { SelectPhysicianDisplay } from "./SelectPhysicianDisplay";
import styled from "@emotion/styled";
import { iconColors } from "../Generic/AppIconColors";
import { fmap } from "../../utils/fmap";
import { PencilSquareIcon } from "../../utils/icones";
import { AppIcon } from "../Generic/AppIcon";
import { emptyStringOrUndefinedToDefault } from "../../formatUtils";
import { primaryPhysicianQueryKey } from "./physician.queryKeys";

export default function PatientPhysicianDetails(props: {
  patientId: PatientId;
  isReadOnly: boolean;
  getPatientPrimaryPhysician: (patientId: PatientId) => Promise<PatientPrimaryPhysician>;
  addNewPhysician: (
    patientId: PatientId,
    params: NewPhysicianParams
  ) => Promise<NewPhysicianCreated>;
  setPatientPhysician: (patientId: PatientId, physicianId: PhysicianId) => Promise<void>;
  getAllPhysicians: (patientId: PatientId) => Promise<PatientAvailablePhysicians>;
  onClickAddNewPhysician: (patientId: PatientId) => void;
}) {
  const [showSelectPhysician, setShowSelectPhysician] = React.useState(false);

  const patientPhysicianQuery = useQuery({
    queryKey: primaryPhysicianQueryKey(props.patientId),
    queryFn: () => props.getPatientPrimaryPhysician(props.patientId),
  });

  const handlePhysicianSave = () => {
    queryClient.invalidateQueries(primaryPhysicianQueryKey(props.patientId));
    setShowSelectPhysician(false);
  };

  const handleClickCloseSelectPhysician = () => {
    setShowSelectPhysician(false);
  };

  switch (patientPhysicianQuery.status) {
    case "loading":
    case "error": {
      return (
        <AppCard className="PatientPhysicianDetailsCard document-question">
          <AppCardHeader>
            <AppCardTitle>{translate(STRINGS.PHYSICIAN_DETAILS)}</AppCardTitle>
          </AppCardHeader>
          <AppCardContent>
            {patientPhysicianQuery.status === "error" ? (
              <Flex alignItems="center" xGap={16}>
                {translate(STRINGS.ERROR_LOADING_DATA)}
              </Flex>
            ) : (
              <LoadingBar />
            )}
          </AppCardContent>
        </AppCard>
      );
    }
    case "success": {
      if (patientPhysicianQuery.data.physician === null || showSelectPhysician) {
        return (
          <SelectPhysicianDisplay
            {...props}
            selectedPhysician={
              fmap(
                patientPhysicianQuery.data.physician,
                (p) =>
                  `${p.firstName} ${p.lastName}, npi: ${emptyStringOrUndefinedToDefault(
                    p.npi,
                    translate(STRINGS.NONE)
                  )}`
              ) ?? undefined
            }
            isReadOnly={props.isReadOnly}
            onSave={handlePhysicianSave}
            onClickAddPhysician={() => {
              setShowSelectPhysician(false);
              props.onClickAddNewPhysician(props.patientId);
            }}
            onClickClose={handleClickCloseSelectPhysician}
          />
        );
      }

      const { physician } = patientPhysicianQuery.data;

      return (
        <AppCard className="PatientPhysicianDetailsCard document-question">
          <AppCardHeaderStyled>
            <AppCardTitle>{translate(STRINGS.PHYSICIAN_DETAILS)}</AppCardTitle>
            {!props.isReadOnly && (
              <AppIcon
                icon={<PencilSquareIcon />}
                onClick={() => setShowSelectPhysician(true)}
                fill={iconColors.get("gray-500")}
              />
            )}
          </AppCardHeaderStyled>
          <AppCardContent>
            <Content>
              <ItemRow>
                <Item>
                  <Label uppercase={false}>{translate(STRINGS.FIRST_NAME)}</Label>
                  <span>{physician.firstName}</span>
                </Item>
                <Item>
                  <Label uppercase={false}>{translate(STRINGS.LAST_NAME)}</Label>
                  <span>{physician.lastName}</span>
                </Item>
              </ItemRow>
              <ItemRow>
                <Item>
                  <Label uppercase={false}>{translate(STRINGS.NPI)}</Label>
                  <span>
                    {emptyStringOrUndefinedToDefault(physician.npi, translate(STRINGS.NONE))}
                  </span>
                </Item>
                <Item>
                  <Label uppercase={false}>{translate(STRINGS.FAX_NUMBER)}</Label>
                  <span>{physician.faxNumber !== null ? physician.faxNumber : "None"}</span>
                </Item>
              </ItemRow>
            </Content>
          </AppCardContent>
        </AppCard>
      );
    }
  }
}

const AppCardHeaderStyled = styled(AppCardHeader)`
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  align-items: center;
`;

const Content = styled(Flex)`
  width: 100%;
  flex-direction: column;
  gap: 8px;
`;

const ItemRow = styled(Flex)`
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
`;

const Item = styled(Flex)`
  flex: 1;
  flex-direction: column;
`;
